/**
 * Fırın konseptli çizimler (Ekşi Maya / Taş Fırın tasarımları, giriş ve ana sayfa).
 * Renkler tasarım belirteçlerinden bağımsız sabit çizim renkleridir (fotoğraf/illüstrasyon gibi).
 */
import { useId } from 'react';

/** Ekşi maya somunu: kabuk degradesi, çizik (scoring), un tozu */
export function SourdoughLoaf({ className, flour = true }: { className?: string; flour?: boolean }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg viewBox="0 0 240 160" className={className} aria-hidden>
      <defs>
        <radialGradient id={`c${id}`} cx="42%" cy="30%" r="75%">
          <stop offset="0" stopColor="#f0b66d" />
          <stop offset=".45" stopColor="#c8762f" />
          <stop offset=".85" stopColor="#8a4316" />
          <stop offset="1" stopColor="#6b3210" />
        </radialGradient>
        <linearGradient id={`s${id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#fbe3bd" />
          <stop offset="1" stopColor="#e9b878" />
        </linearGradient>
      </defs>
      <ellipse cx="120" cy="146" rx="100" ry="10" fill="rgba(59,42,30,.22)" />
      <path d="M18 118 C14 58 66 20 120 20 C174 20 226 58 222 118 C221 134 200 140 120 140 C40 140 19 134 18 118 Z" fill={`url(#c${id})`} />
      <path d="M40 112 C40 70 76 40 120 38" stroke="rgba(255,255,255,.18)" strokeWidth="6" fill="none" strokeLinecap="round" />
      {/* çizik (kulak) */}
      <path d="M62 78 C92 52 148 50 180 74" stroke={`url(#s${id})`} strokeWidth="9" fill="none" strokeLinecap="round" />
      <path d="M62 78 C92 56 148 54 180 74" stroke="#7a3b12" strokeWidth="2" fill="none" strokeLinecap="round" opacity=".55" />
      <path d="M86 100 C104 92 136 92 156 100" stroke={`url(#s${id})`} strokeWidth="6" fill="none" strokeLinecap="round" opacity=".9" />
      {flour && (
        <g fill="#fff8ec" opacity=".85">
          {[[70, 52, 2.2], [96, 40, 1.6], [120, 34, 2.4], [150, 42, 1.8], [172, 56, 2], [84, 64, 1.4], [138, 60, 1.5], [110, 48, 1.2], [160, 66, 1.3], [58, 70, 1.5]].map(([x, y, r], i) => (
            <circle key={i} cx={x} cy={y} r={r} />
          ))}
        </g>
      )}
    </svg>
  );
}

/** Buğday başağı */
export function WheatSprig({ className, color = '#d79a2b' }: { className?: string; color?: string }) {
  const grains = Array.from({ length: 7 }, (_, i) => i);
  return (
    <svg viewBox="0 0 60 200" className={className} aria-hidden>
      <path d="M30 196 C30 150 30 110 31 60" stroke={color} strokeWidth="2.4" fill="none" strokeLinecap="round" />
      {grains.map((i) => {
        const y = 66 + i * 15;
        return (
          <g key={i} fill={color}>
            <ellipse cx="22" cy={y} rx="6.5" ry="11" transform={`rotate(-28 22 ${y})`} opacity={0.92 - i * 0.04} />
            <ellipse cx="39" cy={y + 6} rx="6.5" ry="11" transform={`rotate(28 39 ${y + 6})`} opacity={0.92 - i * 0.04} />
          </g>
        );
      })}
      <ellipse cx="31" cy="50" rx="5.5" ry="10" fill={color} />
      {[[-1, 40], [1, 38], [0, 30]].map(([d, len], i) => (
        <path key={i} d={`M31 44 L${31 + d * 9} ${44 - len}`} stroke={color} strokeWidth="1.2" opacity=".7" />
      ))}
    </svg>
  );
}

/** Taş fırın ağzı: tuğla kemer + ateş ışıltısı + yükselen közler */
export function OvenMouth({ className, embers = 9 }: { className?: string; embers?: number }) {
  const id = useId().replace(/:/g, '');
  return (
    <div className={`relative ${className ?? ''}`} aria-hidden>
      <svg viewBox="0 0 320 220" className="h-full w-full">
        <defs>
          <radialGradient id={`f${id}`} cx="50%" cy="100%" r="80%">
            <stop offset="0" stopColor="#fff1c2" />
            <stop offset=".18" stopColor="#fdba74" />
            <stop offset=".45" stopColor="#f97316" />
            <stop offset=".75" stopColor="#9a3412" />
            <stop offset="1" stopColor="#2a140b" />
          </radialGradient>
          <linearGradient id={`b${id}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#3a2a22" />
            <stop offset="1" stopColor="#1e1510" />
          </linearGradient>
        </defs>
        {/* taş gövde */}
        <path d="M10 214 L10 120 C10 52 78 8 160 8 C242 8 310 52 310 120 L310 214 Z" fill={`url(#b${id})`} />
        {/* tuğla kemer */}
        {Array.from({ length: 13 }, (_, i) => {
          const a = Math.PI - (i / 12) * Math.PI;
          const r1 = 112, r2 = 132, cx = 160, cy = 132;
          const a1 = a - 0.11, a2 = a + 0.11;
          const p = (r: number, ang: number) => `${cx + r * Math.cos(ang)} ${cy - r * Math.sin(ang)}`;
          return <path key={i} d={`M${p(r1, a1)} L${p(r2, a1)} L${p(r2, a2)} L${p(r1, a2)} Z`} fill={i % 2 ? '#7c3a22' : '#8f4528'} stroke="#2a1a12" strokeWidth="2" />;
        })}
        {/* ağız */}
        <path d="M52 214 L52 132 C52 72 100 30 160 30 C220 30 268 72 268 132 L268 214 Z" fill={`url(#f${id})`} />
        {/* odun */}
        <g opacity=".9">
          <rect x="112" y="186" width="96" height="12" rx="6" fill="#3b1d10" transform="rotate(-8 160 192)" />
          <rect x="116" y="186" width="96" height="12" rx="6" fill="#4a2414" transform="rotate(9 160 192)" />
        </g>
        <ellipse cx="160" cy="214" rx="120" ry="10" fill="#120a06" />
      </svg>
      {Array.from({ length: embers }, (_, i) => (
        <span key={i} className="ember" style={{ left: `${30 + ((i * 37) % 40)}%`, bottom: '18%', animationDelay: `${(i * 0.45) % 3.6}s` }} />
      ))}
    </div>
  );
}

/** Küçük ekmek / simit / kruvasan ikon seti (boş durum ve dekor) */
export function BakeryDecor({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 120 40" className={className} aria-hidden>
      <g fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" stroke="currentColor" opacity=".55">
        <path d="M8 30 C6 18 14 10 24 10 C34 10 42 18 40 30 Z" />
        <path d="M16 18 l6 6 M24 15 l6 6 M32 18 l4 4" />
        <circle cx="62" cy="22" r="11" /><circle cx="62" cy="22" r="4.5" />
        <path d="M84 30 C88 16 104 14 112 26 M88 28 C92 20 102 20 106 28 M96 30 C97 24 101 23 103 27" />
      </g>
    </svg>
  );
}
